import { call, put, select, takeLeading, takeLatest } from "redux-saga/effects";
import { push } from "connected-react-router";
import { Routes } from "../../const/routes";
import {
  fetchingProductDetailDone,
  getComplexProduct,
  getComplexProductFailure,
  getComplexProductSuccess,
  getComplexProductDetail,
  getComplexProductDetailFailure,
  getComplexProductDetailSuccess,
  closeLoadingIndex,
} from "redux/reducers/product";
import { changeUnitDisplaySetting, initSubscription } from "redux/reducers/subscription";
import { ProductAPI } from "services/product";
import { compact } from "lodash";
import { CentralModalType, CustodianRegisterStatus, ErrorType, RiskProfileErrors, UnitDisplay } from "const";
import { openModal } from "redux/reducers/central-modal";
import { isBahtDisplay, isSecurityTypeCOM, unitToBaht } from "utils/product-order-util";
import { getRoutePath } from "const/routes";
import { convertToThaiFormat, DateFormat_1, DateFormat_2 } from "utils/date";
import { AcceptType, DisclaimerType } from "const";
import { checkRiskProfile, isPassedTheBankruptcyQualificationCheck } from "./customer";
import { checkRegister } from "./disclaimer";
import { setRedirectUrl } from "redux/reducers/redirect";

import { setSuitabilityResult, setRegisteredStatus } from "redux/reducers/customer";
import { showSuitabilityRequiredModal } from "redux/reducers/modals/suitability";
import { clearLoadingIndex } from "redux/reducers/ultrahigh-home";
import flatten from "lodash/flatten";
import { RouterSelector } from "redux/selector";
import { StepControl } from "utils/misc/step-control";
import store from "redux/store";
import { RiskProfileError } from "classes/error/RiskProfileError";
import { PerpBondAPI } from "services/perp-bond";
import { selectPerpBondSuccess, setRequireProductTestModalVisible } from "redux/reducers/perp-bond";
import { isMissingTaxIdError, isSystemCloseError } from "const/const-helper";
import { PreTestSection } from "routes/PerpetualBond/const";
import { getPromise } from "utils/common-util";

function* getComplexProductSaga() {
  try {
    const response = yield call(ProductAPI.getComplexProduct);
    yield put(getComplexProductSuccess(response.data.result.securities));
  } catch (error) {
    //* tax id validation
    if (isMissingTaxIdError(error?.code)) {
      error.ignore = true;
    }

    if (isSystemCloseError(error?.code)) {
      yield put(getComplexProductSuccess([]));
    }

    yield put(getComplexProductFailure(error));
  }
}

const getComplexProductDetailSaga = function* ({ payload: { product, clearLoadingStatus } }) {
  let alreadyClearLoading = false;
  try {
    const validSuitability = yield call(checkRiskProfile);
    const validRegister = yield call(checkRegister);

    yield put(setRegisteredStatus(validRegister));
    yield put(setSuitabilityResult(validSuitability));

    if (!validRegister) {
      yield put(
        setRedirectUrl({
          id: "suitability",
          url: (yield select(RouterSelector.location)).pathname,
          replace: true,
          dispatchAction: getComplexProductDetail({ product }),
        }),
      );
      yield put(push(getRoutePath(Routes.Register.CustomerInfo)));
      yield put(fetchingProductDetailDone());
      return;
    }

    if (!validSuitability) {
      yield put(
        showSuitabilityRequiredModal({
          redirectConfig: {
            url: (yield select(RouterSelector.location)).pathname,
            replace: true,
            dispatchAction: getComplexProductDetail({ product }),
          },
        }),
      );
      yield put(fetchingProductDetailDone());
      return;
    }

    if (!(yield call(isPassedTheBankruptcyQualificationCheck))) {
      return;
    }

    const response = yield call(ProductAPI.getProductDetail, { securityCode: product.securityCode });

    yield put(fetchingProductDetailDone());
    const {
      acceptRiskLevel,
      acceptFxRate,
      acceptFutureRisk,
      acceptNoneHNW,
      acceptHNWS,
      acceptHNW,
      acceptHNWW,
      acceptHNWE,
      acceptUHNWW,
      acceptUHNWE,
      acceptVulnerable,
      icLicenseNo,
      mncQuotaId,
      icName,
      detail: productDetail,
      osn,
      osnCustodianAccount,
      osnCustodianLAccount,
      lombard: canActiveLombardFlow,
      accAlertType: globalSavingsAlertType,
      accAlertMessage: globalSavingsAlertMessage,
      sfcdCurrencyCodes: globalSavingsSfcdCurrencyCodes,
    } = response.data.result;

    if (clearLoadingStatus && !alreadyClearLoading) {
      alreadyClearLoading = true;
      yield put(clearLoadingIndex());
    }

    productDetail.isSecurityOffshoreNote = !!productDetail.offshoreNote;
    productDetail.isPremiumDiscount = productDetail.sellParValue !== productDetail.sellPriceUnit;
    productDetail.isTaxApplicable = !!productDetail.hasWht;

    // In case wht is 0, force UI to behave as normal (not as premium-discount with tax).
    if (!productDetail.hasWht) {
      // Removed because this is still premium-discount, just without tax.
      // productDetail.isPremiumDiscount = false;
      productDetail.isTaxApplicable = false;
    }

    if (productDetail.isSecurityOffshoreNote) {
      productDetail.osnDetail = {
        osn: osn,
        osnCustodianAccount: osnCustodianAccount,
        osnCustodianLAccount: osnCustodianLAccount,
      };
    }

    // check crs for offshore note
    if (
      productDetail.isSecurityOffshoreNote &&
      (osn === CustodianRegisterStatus.REGISTER.code || osn === CustodianRegisterStatus.EXPIRED.code)
    ) {
      if (osn === CustodianRegisterStatus.REGISTER.code) {
        yield put(
          openModal({
            code: AcceptType.OffshoreNonFaceCRS,
            state: { securityCode: productDetail.securityCode },
            actions: {
              onAccept: () => {
                store.dispatch(push(`${Routes.Custodian}?as=${product.securityCode}`));
              },
            },
          }),
        );
      } else {
        yield put(
          openModal({
            code: CentralModalType.OffshoreInformPopups,
            state: { status: CustodianRegisterStatus.EXPIRED.code },
            actions: {
              onAccept: () => {
                store.dispatch(
                  openModal({
                    code: AcceptType.OffshoreNonFaceCRS,
                    state: { securityCode: productDetail.securityCode },
                    actions: {
                      onAccept: () => {
                        store.dispatch(push(`${Routes.Custodian}?as=${product.securityCode}`));
                      },
                    },
                  }),
                );
              },
            },
          }),
        );
      }

      return;
    }

    // check Global Savings account
    if (globalSavingsAlertType || globalSavingsAlertMessage) {
      const { promise, resolve } = getPromise();
      yield put(
        openModal({
          code: CentralModalType.ValidateDepositAccount,
          state: {
            accAlertType: globalSavingsAlertType,
            accAlertMessage: globalSavingsAlertMessage,
            sfcdCurrencyCodes: globalSavingsSfcdCurrencyCodes,
            source: "COM",
          },
          actions: {
            onNext: () => {
              resolve(true);
            },
            onCancel: () => {
              resolve(false);
            },
          },
        }),
      );

      const next = yield promise;
      if (!next) {
        if (clearLoadingStatus) {
          yield put(clearLoadingIndex());
        } else {
          yield put(closeLoadingIndex());
        }
        return;
      }
    }

    // check pre test
    let haveKaTest = false;
    if (productDetail.preSubId) {
      const preSubPayloadRequest = {
        preSubId: productDetail.preSubId,
        securityCode: productDetail.securityCode,
      };
      const responseProductTest = yield call(PerpBondAPI.getPerpBondDetail, preSubPayloadRequest);
      const testSuit = responseProductTest.data.result?.preSubTestSecuritiesDetail ?? {};
      const someTestNotDone = [
        testSuit.video?.status,
        testSuit.productTest?.status,
        testSuit.precaution?.status,
        testSuit.ka?.status,
        testSuit.risk?.status,
      ].some((s) => s === false);

      haveKaTest = !!testSuit.ka;

      if (someTestNotDone) {
        testSuit.section = PreTestSection.PrimaryMarket;
        yield put(selectPerpBondSuccess(testSuit));
        yield put(setRequireProductTestModalVisible(true));
        return;
      }
    }

    const acceptRiskDesc = productDetail.riskDesc ? AcceptType.RiskDesc : null;
    const offshoreTermAndConditions =
      productDetail.isSecurityOffshoreNote && productDetail.offshoreNoteTermsConditionFilePath
        ? DisclaimerType.OffshoreTermAndConditions
        : null;
    const acceptPDPACOMP = !haveKaTest ? DisclaimerType.PDPA_COMP : null;

    // please preserve order of accepted modal
    const requireAcceptDisclaimerList = compact([
      acceptPDPACOMP,
      acceptHNW,
      acceptRiskLevel,
      acceptFxRate,
      acceptFutureRisk,
      acceptHNWS,
      acceptNoneHNW,
      acceptHNWW,
      acceptHNWE,
      acceptUHNWW,
      acceptUHNWE,
      acceptVulnerable,
      acceptRiskDesc,
      offshoreTermAndConditions,
    ]);
    const acceptDiscList = flatten(
      yield new StepControl(requireAcceptDisclaimerList, (code, [next, prev, , forceEnd]) => {
        const backOnCancel = [DisclaimerType.HNW_EXPERIENCE, DisclaimerType.UHNW_EXPERIENCE].includes(code);
        const actions = {
          onAccept: next,
          onCancel: backOnCancel ? prev : forceEnd,
        };
        const state = (() => {
          if ([ErrorType.AGE_OVER_60, ErrorType.ATTITUDE, ErrorType.EXPERIENCE].includes(code)) {
            return { ...productDetail };
          }
          if (code === DisclaimerType.HNW) {
            return { title: "modal.disclaimer.header.for-uhnw" };
          }
          if (code === AcceptType.RiskDesc) {
            return { ...productDetail };
          }
          if (code === DisclaimerType.PDPA_COMP) {
            return { ...productDetail };
          }
          if (code === DisclaimerType.OffshoreTermAndConditions) {
            return { pdfUrl: productDetail.offshoreNoteTermsConditionFilePath };
          }
        })();
        store.dispatch(openModal({ code, state, actions }));
      }).start(),
    );

    const acceptedAll = requireAcceptDisclaimerList.every((disc) => acceptDiscList.includes(disc));
    if (acceptedAll) {
      const defaultPurchaseUnit = productDetail.quotaUnit ?? productDetail.sellMaxUnit;
      const initSubscriptionPayload = {
        defaultPurchaseUnit,
        canActiveLombardFlow,
        acceptDiscList: acceptDiscList.filter((disc) => typeof disc === "string"),
        attachDocuments: flatten(
          acceptDiscList.filter((fileArray) => Array.isArray(fileArray) && fileArray.every((file) => file.isFile)),
        ),
        mncQuotaId,
        icLicenseNo: icLicenseNo,
        icName: icName,
        unit: defaultPurchaseUnit,
        baht: unitToBaht(defaultPurchaseUnit, productDetail.sellPriceUnit),
      };

      productDetail.sellMaxUnitOrigin = productDetail.sellMaxUnit;
      if (productDetail.ccy) {
        if (isBahtDisplay(productDetail.ccy)) {
          productDetail.ccy = null;
        } else {
          productDetail.ccy = productDetail.ccy.toUpperCase();
        }
      }
      productDetail.paymentAccountCcy = productDetail.paymentAccountCcy ?? "THB";

      yield put(getComplexProductDetailSuccess(productDetail));
      yield put(initSubscription(initSubscriptionPayload));

      if (isSecurityTypeCOM(productDetail)) {
        if (productDetail.sellParValue !== productDetail.sellPriceUnit) {
          yield put(changeUnitDisplaySetting(UnitDisplay.UNIT));
        } else {
          yield put(changeUnitDisplaySetting(productDetail.ccy ?? UnitDisplay.BAHT));
        }
      }

      yield put(push(getRoutePath(Routes.Primary.ComplexProductDetail, product.securityCode)));
    } else {
      yield put(getComplexProductDetailFailure({ message: `cancel some require disclaimer`, ignore: true }));
    }
  } catch (error) {
    switch (error.code) {
      case "4501": // Out of time
        const substActiveStartTime = product.substActiveStartTime.replace(/(\d{2})(\d{2})/, `$1:$2`);
        const substActiveEndTime = product.substActiveEndTime.replace(/(\d{2})(\d{2})/, `$1:$2`);
        yield put(getComplexProductDetailFailure({ ...error, params: [substActiveStartTime, substActiveEndTime] }));
        break;

      case "4505": // Out of date
        const substStartDate = convertToThaiFormat(product.substStartDate, DateFormat_2);
        const substEndDate = convertToThaiFormat(product.substEndDate, DateFormat_2);
        yield put(getComplexProductDetailFailure({ ...error, params: [substStartDate, substEndDate] }));
        break;

      case "43948": // date limit
        const ageLimitDate = convertToThaiFormat(product.sellAgeLimitDate, DateFormat_1);
        yield put(getComplexProductDetailFailure({ ...error, params: [ageLimitDate] }));
        break;

      //* tax id validation
      case ErrorType.TAX_ID_MISSING_1:
      case ErrorType.TAX_ID_MISSING_2:
        if (isMissingTaxIdError(error?.code)) {
          error.ignore = true;
        }
        yield put(getComplexProductDetailFailure(error));
        break;

      default:
        if (error instanceof RiskProfileError) {
          yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
          yield put(getComplexProductDetailFailure({ ...error, ignore: true }));
        } else {
          yield put(getComplexProductDetailFailure(error));
        }
        break;
    }
  } finally {
    if (clearLoadingStatus && !alreadyClearLoading) {
      yield put(clearLoadingIndex());
    }
  }
};

const saga = [
  takeLeading(getComplexProduct.type, getComplexProductSaga),
  takeLatest(getComplexProductDetail.type, getComplexProductDetailSaga),
];

export default saga;
